/**
 * **Stabilize** — the mech self-maintenance Full action.
 *
 * RAW (PC version, Core Book p.70): when you Stabilize you choose **one** primary —
 *   - **Cool** — clear ALL heat and remove the **Exposed** status; or
 *   - **Patch** — spend **one repair** from your Repair Cap to restore **all** your HP —
 * **then** choose **one** secondary —
 *   - reload all **Loading** weapons; or
 *   - clear **Burn** currently affecting you; or
 *   - clear **one** condition affecting you (Immobilized / Impaired / Jammed / Lock On /
 *     Shredded / Slowed / Stunned); or
 *   - end one ongoing harmful effect on you that a save would end.
 *
 * **This module is the resolution half** — the rules-side producer of the recorded
 * `apply_resolved` step list (draw-and-record). Stabilize draws **no dice** — it is fully
 * deterministic — so unlike an attack/barrage there is no `InputSource`; the steps are computed
 * straight off the actor's tracked state.
 *
 * **It reuses the existing models, it does not fork them:**
 *   - Patch is an **in-combat consumer of the shared `repair_cap` resource** (`REPAIR_CAP_RESOURCE`,
 *     seeded at spawn — `spawn.ts`; read via `commit.ts`). It decrements that combat-scoped
 *     resource by one (`resource_delta -1`) and restores HP — NOT a second repair-cap source of
 *     truth. The repair-cap **hard-reject at 0** is the
 *     gameplay-tier wall in `economy.ts` (`checkStabilizeLegality`), run before this resolves.
 *   - Cool reuses the heat track (`heat_delta`) + the Exposed status (`status_remove`).
 *   - The Burn secondary reuses the Burn model (`burn_set 0`); the condition/save-effect
 *     secondaries are `status_remove` steps.
 *
 * **The PC menu is NOT baked in.** {@link stabilizeSteps} is **choice-driven** — it takes the
 * resolved primary + secondary and emits exactly their steps. The NPC variant (heat + Exposed +
 * reload only — no HP heal, no condition clear) selects a SUBSET of the same
 * primitive (`{ primary: "cool", secondary: "reload" }`), so it never carries the PC condition-clear
 * path.
 */

import type { Combatant, EncounterState, ResolvedStep, ApplyResolvedCommand } from "@lancer-vtt/domain";
import { REPAIR_CAP_RESOURCE, hasStatus, weaponInstanceKey, loadedResourceKey, isWeaponLoaded } from "@lancer-vtt/domain";
import type { RequestStabilize, StabilizePrimary, StabilizeSecondary } from "@lancer-vtt/shared";
import { ResolveError, type Resolved, type ResolveResult } from "./resolve";
import { spendStepsNow } from "./events/gameEvents";
import type { EventLoop } from "./events/loop";
import { raiseHeatCleared } from "./events/trackEvents";

/** The canonical **Exposed** status name (cleared by the Cool primary). */
export const EXPOSED_STATUS = "Exposed";

/**
 * The conditions a PC Stabilize's **clear one condition** secondary may end (RAW Core Book
 * p.70 — "such as Immobilized or Jammed"; the full listed set). The CLI/UI offers exactly
 * these; the engine itself clears whatever status name it is handed (the list is the menu's,
 * not a resolution-time wall — a status the actor doesn't carry is a no-op + a warning).
 */
export const STABILIZE_CLEARABLE_CONDITIONS: readonly string[] = [
  "Immobilized",
  "Impaired",
  "Jammed",
  "Lock On",
  "Shredded",
  "Slowed",
  "Stunned",
];

/** The resolved choice a {@link stabilizeSteps} call applies (the PC intent's choices, or an NPC subset). */
export type StabilizeChoice = {
  primary: StabilizePrimary;
  secondary?: StabilizeSecondary;
  /** The status name for `clear_condition` / `end_save_effect`. */
  secondaryStatus?: string;
};

/**
 * Compute the recorded {@link ResolvedStep} list for a Stabilize **off the actor's tracked
 * state** — the shared, choice-driven primitive both the PC intent and (later) the NPC variant
 * fold through. Deterministic (no dice).
 *
 * **Primary** (always emits at least one step so the Full action is always charged + undoable):
 *   - `cool` → `heat_delta -(current heat)` (vent to 0; emitted even at 0 heat so the action is
 *     recorded), plus `status_remove "Exposed"` **only when the actor carries it**.
 *   - `patch` → `resource_delta repair_cap -1` (the spend — always emitted; the at-0 reject is the
 *     `economy.ts` wall), plus `hp_delta +(maxHp − current HP)` **only when below full** (the
 *     effective delta, so the inverse round-trips exactly).
 *
 * **Secondary** (each emits only when it changes something, per the round-trip convention):
 *   - `reload` → `resource_set loaded:<key> = 1` for each of the actor's **Loading** weapons
 *     currently unloaded; a weapon already loaded (or no Loading weapon at all) contributes no
 *     step, just a warning, per the round-trip convention. The NPC Stabilize variant (`{primary:"cool",
 *     secondary:"reload"}`) rides this same primitive for free.
 *   - `clear_burn` → `burn_set 0` only when marked burn > 0.
 *   - `clear_condition` / `end_save_effect` → `status_remove <secondaryStatus>` only when the actor
 *     carries that status (else a warning — nothing to clear).
 *
 * @returns the steps (in apply order) + any advisory warnings (a stub no-op / nothing-to-clear note).
 */
export function stabilizeSteps(actor: Combatant, choice: StabilizeChoice): { steps: ResolvedStep[]; warnings: string[] } {
  const steps: ResolvedStep[] = [];
  const warnings: string[] = [];

  // ── primary ──
  if (choice.primary === "cool") {
    // Clear all heat — vent to 0. Emitted even when heat is already 0 so a Cool always
    // records a grounded step (the action is always charged + undoable as one unit). The
    // recorded delta is the EFFECTIVE change (heat → 0), so the inverse round-trips.
    const heat = actor.current.heat ?? 0;
    steps.push({ op: "heat_delta", target: actor.id, delta: -heat });
    // Remove Exposed — only when carried (idempotent at playback, but emit only on a real change).
    if (hasStatus(actor.statuses, EXPOSED_STATUS)) {
      steps.push({ op: "status_remove", target: actor.id, status: EXPOSED_STATUS });
    }
  } else {
    // Patch — spend one repair from the shared Repair Cap resource (in-combat consumer; the
    // at-0 reject is `checkStabilizeLegality` in economy.ts, run before this). Always emitted —
    // a Patch costs the repair regardless of how much HP it restores.
    steps.push(...spendStepsNow([{ kind: "counter", carrier: actor.id, key: REPAIR_CAP_RESOURCE, delta: -1 }], undefined, undefined));
    // Restore ALL HP — the effective delta to full (only when below full, so the inverse
    // round-trips exactly at the cap).
    const heal = actor.base.maxHp - actor.current.hp;
    if (heal > 0) steps.push({ op: "hp_delta", target: actor.id, delta: heal });
  }

  // ── secondary ──
  switch (choice.secondary) {
    case undefined:
      break;
    case "reload": {
      // Reload every currently-UNLOADED Loading weapon on the loadout — `resource_set
      // loaded:<key> = 1`, the inverse-shape of the fire-emit in
      // `resolve/weaponConsume.ts`. Skips an already-loaded weapon (nothing to change, mirrors `clear_burn`'s
      // "only when > 0" convention); alt firing profiles share their parent's instance key, so
      // iterating the top-level loadout (not into `.profiles`) already covers every mode.
      const loadingWeapons = (actor.base.weapons ?? []).filter((w) => w.loading);
      let reloaded = 0;
      for (const w of loadingWeapons) {
        const instKey = weaponInstanceKey(w);
        if (isWeaponLoaded(actor.current.resources, instKey)) continue; // already loaded.
        const key = loadedResourceKey(instKey);
        const prev = actor.current.resources?.[key] ?? 0;
        steps.push({ op: "resource_set", target: actor.id, key, val: 1, prev });
        reloaded++;
      }
      if (reloaded === 0) {
        warnings.push(
          loadingWeapons.length === 0
            ? `stabilize secondary 'reload': ${actor.name} carries no Loading weapons — nothing to reload`
            : `stabilize secondary 'reload': ${actor.name}'s Loading weapons are already loaded — nothing to reload`,
        );
      }
      break;
    }
    case "clear_burn": {
      const burn = actor.current.burn ?? 0;
      if (burn > 0) steps.push({ op: "burn_set", target: actor.id, burn: 0, prev: burn });
      else warnings.push(`stabilize secondary 'clear_burn': ${actor.name} carries no burn — nothing to clear`);
      break;
    }
    case "clear_condition":
    case "end_save_effect": {
      const name = choice.secondaryStatus;
      if (name === undefined || name === "") {
        warnings.push(`stabilize secondary '${choice.secondary}': no status named — nothing to clear`);
      } else if (hasStatus(actor.statuses, name)) {
        steps.push({ op: "status_remove", target: actor.id, status: name });
      } else {
        warnings.push(`stabilize secondary '${choice.secondary}': ${actor.name} does not carry "${name}" — nothing to clear`);
      }
      break;
    }
  }

  return { steps, warnings };
}

/** Named arguments for {@link resolveStabilize}. */
export type ResolveStabilizeArgs = {
  state: EncounterState;
  intent: RequestStabilize;
  /** Who the resolution is attributed to (the issuing slot); stamped on the command. Defaults to `"authority"`. */
  actorId?: string;
  /** The intent's event loop: a Cool that vents heat raises `heat_cleared` into it. */
  loop?: EventLoop;
};

/**
 * Resolve a {@link RequestStabilize} into ONE recorded `apply_resolved` (`kind: "stabilize"`).
 * Deterministic — no dice. Delegates the per-choice step list to {@link stabilizeSteps} and wraps
 * it; the caller (the room) folds the Full-action cost into a `batch` with one `spend_action`, so
 * the whole Stabilize is one atomic charge + one logical undo.
 *
 * @throws {ResolveError} if the actor id is unknown in `state`.
 */
export function resolveStabilize(args: ResolveStabilizeArgs): ResolveResult {
  const { state, intent, actorId = "authority", loop } = args;
  const actor = state.combatants[intent.actor];
  if (!actor) throw new ResolveError(`resolveStabilize: unknown combatant ${intent.actor}`);
  const { steps, warnings } = stabilizeSteps(actor, {
    primary: intent.primary,
    secondary: intent.secondary,
    secondaryStatus: intent.secondaryStatus,
  });
  if (intent.primary === "cool") raiseHeatCleared(loop, { combatant: actor.id, before: actor.current.heat ?? 0, after: 0 });
  const command: ApplyResolvedCommand = { type: "apply_resolved", actorId, steps, kind: "stabilize" };
  const resolved: Resolved = { kind: "resolved", command, steps, warnings };
  return resolved;
}
